import { useMutation } from '@tanstack/react-query';
import { Alert, Button, Typography } from 'antd';
import React, { useState } from 'react';
import Icon from '@ant-design/icons';
import { type IntegrationInstallInstructionsProps } from '@pages/AdminSettings/components/IntegrationTab/constants';
import { MONDAY_CLIENT_ID } from '@config/constants';
import { apiClient } from '@api/client';
import { IntegrationsMondayIcon } from '@icons';

const { Title, Paragraph, Link } = Typography;

const MONDAY_INSTALL_URL = `https://auth.monday.com/oauth2/authorize?client_id=${MONDAY_CLIENT_ID}&response_type=install`;

export default function MondayInstallInstructions({
  organizationId,
  onClose
}: IntegrationInstallInstructionsProps) {
  const [notFound, setNotFound] = useState(false);

  const verify = useMutation({
    mutationFn: async () => {
      const { data } =
        await apiClient.integration.integrationControllerGetInsalledRegistrations(
          { organizationId }
        );
      return data.integrations.some(
        (i) => i.type === 'monday' && i.status === 'installed'
      );
    },
    onSuccess: (installed) => {
      if (installed) {
        onClose();
      } else {
        setNotFound(true);
      }
    }
  });

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      <Title level={3}>Connect monday.com to StreamWork</Title>

      <Paragraph>
        Follow these quick steps and you&apos;ll be up and running.
      </Paragraph>

      <ol>
        <li>
          <Paragraph style={{ marginBottom: 8 }}>
            Install the StreamWork app in monday.com.
          </Paragraph>
          <Button
            type="primary"
            onClick={() => window.open(MONDAY_INSTALL_URL, '_blank')}
            style={{ marginBottom: 16 }}
          >
            <Icon component={IntegrationsMondayIcon} />
            Open monday.com install page
          </Button>
        </li>
        <li>
          In monday.com, create a new workflow (or open an existing one) that
          uses a StreamWork-provided action such as{' '}
          <strong>Create Approval</strong>.
        </li>
        <li>
          In the action&apos;s configuration panel, click the{' '}
          <strong>StreamWork account</strong> field and complete the sign-in
          step to link your StreamWork account. This step is what marks the
          integration as installed in StreamWork.
        </li>
        <li>
          Finish configuring and save the workflow in monday.com, then return
          here and click <strong>Done</strong> below.
        </li>
      </ol>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="New to monday.com workflows?"
        description={
          <Link
            href="https://support.monday.com/hc/en-us/articles/11065311570066-Get-started-with-AI-workflows"
            target="_blank"
          >
            monday.com – Getting started with workflows ↗
          </Link>
        }
      />

      {notFound && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Connection not detected yet"
          description="It looks like step 3 hasn't been completed. Go back to your monday.com workflow, click the StreamWork account field, and finish the sign-in pop-up. Then try again."
        />
      )}

      <div
        style={{
          display: 'flex',
          marginTop: 'auto',
          justifyContent: 'flex-end',
          gap: 8
        }}
      >
        <Button onClick={onClose}>Cancel</Button>
        <Button
          type="primary"
          loading={verify.isLoading}
          onClick={() => {
            setNotFound(false);
            verify.mutate();
          }}
        >
          Done — verify connection
        </Button>
      </div>
    </div>
  );
}
